import { Component } from '@angular/core';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { ButtonModule, ButtonOptions } from '@usitsdasdesign/dds-ng/button';
import { DropdownModule, DropdownOptions, DropdownItemOptions } from '@usitsdasdesign/dds-ng/dropdown';
import { InputModule, DescriptionType, TextAlign, InputOptions } from '@usitsdasdesign/dds-ng/input';
import { ExtThemes, ButtonKind, Size, WidthState, LabelPosition, ErrorState } from '@usitsdasdesign/dds-ng/shared';
import { TabsModule } from '@usitsdasdesign/dds-ng/tabs';
import { TextareaModule, TextareaOptions } from '@usitsdasdesign/dds-ng/textarea';
import { Specification } from '../../core/models/specification.models';
import { ServicesService } from '../../core/services/services.service';
import { SpecificationsService } from '../../core/services/specifications.service';
import { getSpecificationLabel } from '../../core/utils/label.utils';

@Component({
  selector: 'app-edit-service',
  standalone: true,
  imports: [ButtonModule, TabsModule, DropdownModule, RouterModule, InputModule, TextareaModule, ReactiveFormsModule],
  templateUrl: './edit-service.component.html',
  styleUrl: './edit-service.component.scss',
})
export class EditServiceComponent {
  projectId: string;
  serviceId: string;
  serviceTypeId: string;
  specificationId: string;
  modelId: string;
  specifications: Specification[];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private servicesService: ServicesService,
    private specificationsService: SpecificationsService,
  ) {}

  serviceTypeDropdownOptions: DropdownOptions = {
    label: 'API Simulation',
    ariaLabel: 'service type dropdown',
    theme: ExtThemes.dark,
    kind: ButtonKind.primary,
    size: Size.md,
    width: WidthState.fixed,
    isColorBg: false,
    isInverse: false,
    customClass: '',
    disabled: false,
    isResponsive: false,
    stickerIsOutsideClick: false,
    stickerShift: 0,
    stickerMaxHeight: '',
    stickerIsDisabled: false,
    stickerPosition: 'bottom-left',
    stickerIndent: 0,
    stickerCustomClass: '',
    stickerIsDynamic: true,
  };

  serviceTypeDropdownItems: DropdownItemOptions[];

  serviceTypeSelected(value: string) {
    this.serviceTypeId = value;
  }

  specificationDropdownOptions: DropdownOptions = {
    label: '',
    ariaLabel: 'api specification dropdown',
    theme: ExtThemes.dark,
    kind: ButtonKind.primary,
    size: Size.md,
    width: WidthState.fixed,
    isColorBg: false,
    isInverse: false,
    customClass: '',
    disabled: false,
    isResponsive: false,
    stickerIsOutsideClick: false,
    stickerShift: 0,
    stickerMaxHeight: '',
    stickerIsDisabled: false,
    stickerPosition: 'bottom-left',
    stickerIndent: 0,
    stickerCustomClass: '',
    stickerIsDynamic: true,
  };

  specificationDropdownItems: DropdownItemOptions[];

  specificationSelected(value: string) {
    this.setSpecificationContent(value);
  }

  inputFormCtrl = new FormControl({ value: '', disabled: false });

  inputOptions: InputOptions = {
    label: '',
    labelPosition: LabelPosition.external,
    placeholder: '',
    isError: false,
    isRequired: true,
    isInverse: false,
    size: Size.md,
    description: '',
    descriptionType: DescriptionType.text,
    errorMessage: '',
    errorState: ErrorState.default,
    textAlign: TextAlign.left,
    type: 'text',
  };

  changeValue() {}

  onBlur() {}

  textareaFormCtrl = new FormControl({ value: '', disabled: false });

  textareaOptions: TextareaOptions = {
    label: '',
    labelPosition: LabelPosition.external,
    placeholder: '',
    minHeight: 250,
    isRequired: false,
    isInverse: false,
    readonly: false,
    isError: true,
    errorState: ErrorState.default,
    description: '',
    errorMessage: '',
    size: Size.md,
  };

  descriptionValueChanged() {
    console.log('descriptionValueChanged', this.textareaFormCtrl.value);
  }

  addServiceButtonOptions: ButtonOptions = {
    theme: ExtThemes.green,
    kind: ButtonKind.primaryLoud,
    size: Size.md,
    width: WidthState.fixed,
    isLoading: false,
    icon: 'dds-icon_plus',
    isIconLeft: false,
    isColorBg: false,
    isInverse: false,
    disabled: false,
    ariaLabel: 'Add Service',
    customClass: '',
    role: 'button',
  };

  addServiceClick(): void {
    // TODO: add logic for service type
    this.servicesService
      .updateService(this.projectId, this.serviceId, {
        name: this.inputFormCtrl.value ?? '',
        description: this.textareaFormCtrl.value ?? '',
        type: 'standard',
        project: this.projectId,
        specification: this.specificationId,
        model: this.modelId,
      })
      .subscribe((e) => {
        console.log(e);
        this.router.navigate(['../services'], { relativeTo: this.route });
      });
  }

  cancelButtonOptions: ButtonOptions = {
    theme: ExtThemes.green,
    kind: ButtonKind.primary,
    size: Size.md,
    width: WidthState.fixed,
    isLoading: false,
    icon: '',
    isIconLeft: true,
    isColorBg: false,
    isInverse: false,
    disabled: false,
    ariaLabel: 'Cancel',
    customClass: '',
    role: 'button',
  };

  cancelClick(event: Event): void {
    this.router.navigate(['../services'], { relativeTo: this.route });
  }

  ngOnInit(): void {
    this.serviceTypeDropdownItems = [];
    this.specificationDropdownItems = [];
    this.specifications = [];

    this.route.parent?.paramMap.subscribe((params) => {
      this.projectId = params.get('id') ?? '';
      this.serviceId = this.route.snapshot.params['serviceId'];

      this.servicesService.getServiceById(this.projectId, this.serviceId).subscribe((response) => {
        this.inputFormCtrl.setValue(response.name);
        this.textareaFormCtrl.setValue(response.description);
        this.serviceTypeId = response.type;
        this.specificationId = response.specification;
        this.modelId = response.model;

        this.setSpecificationContent(this.specificationId);
        this.serviceTypeDropdownOptions.label = response.type;
      });

      this.specificationsService.getSpecificationsForProject(this.projectId).subscribe((response) => {
        this.specifications = response == null ? [] : response;
        this.populateDropdown();
      });
    });
  }

  private populateDropdown(): void {
    if (this.specifications.length > 0) {
      this.specificationDropdownOptions.label = getSpecificationLabel(this.specifications[0]);

      for (let specification of this.specifications) {
        this.specificationDropdownItems.push({
          heading: getSpecificationLabel(specification),
          value: specification.id,
        });
      }
    }
  }

  private setSpecificationContent(id: string) {
    let specification = this.specifications.find((x) => x.id == id);
    if (specification == undefined) return;
    this.specificationId = id;
    this.specificationDropdownOptions.label = getSpecificationLabel(specification);
  }
}
